import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Account } from '@pages/Trade/types'

import { useSessionEntity } from '@context/EntityContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { TickmillCompaniesEnum } from '@utils/companyName.utils'

import { AccountGroupType } from '@model/AccountGroupType'
import { PlatformTypeEnum } from '@model/PlatformTypeEnum'
import { isTradingAccountStatusReadOnly } from '@model/TradingAccount'

import { safeLocalStorage } from '@hooks/useStorage'

export const useTradeLogin = () => {
  const location = useLocation()
  const apiClient = useApiClient(ClientApiClient)
  const entity = useSessionEntity()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [showAccountOpening, setShowAccountOpening] = useState(false)
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null)
  const [allActiveAccounts, setAllActiveAccounts] = useState<Account[]>([])

  const selectedAccount = useMemo(
    () => allActiveAccounts.find((a) => a.id === selectedAccountId) ?? null,
    [allActiveAccounts, selectedAccountId]
  )

  const loginToAccount = useCallback(
    async (account: Account) => {
      try {
        setError(null)

        const isTMTAccount = account.platformType.id === PlatformTypeEnum.TickmillTrader

        if (isTMTAccount && account.id && account.externalId) {
          await apiClient.getTATradeSSOToken(account.id)
        }

        await saveLastLoggedInAccount(account)
        setSelectedAccountId(account.id)
        setIsLoggedIn(true)

        return true
      } catch (err) {
        console.error('Platform verification failed:', err)

        const isTMTAccount = account.platformType.id === PlatformTypeEnum.TickmillTrader
        if (isTMTAccount) {
          setError('platform_down')
        }

        return false
      }
    },
    [apiClient]
  )

  const updateSelectedAccount = useCallback(
    async (account: Account) => {
      try {
        await loginToAccount(account)
      } catch (err) {
        console.error('Failed to update account:', err)
      }
    },
    [loginToAccount]
  )

  useEffect(() => {
    const stateAccount = location.state?.selectedTradingAccount

    if (stateAccount && allActiveAccounts.length > 0) {
      const accountToSelect = allActiveAccounts.find((acc) => acc.id === stateAccount.id)
      accountToSelect && loginToAccount(accountToSelect)
    }
  }, [location.state, allActiveAccounts, loginToAccount])

  useEffect(() => {
    if (entity !== TickmillCompaniesEnum.TICKMILL_SC) {
      setLoading(false)
      return
    }

    const init = async () => {
      try {
        setLoading(true)
        setError(null)

        const [liveResponse, demoResponse] = await Promise.all([
          apiClient.getTradingAccounts({
            pageSize: 100,
            pageIndex: 1,
          }),
          apiClient.getDemoTradingAccounts({
            pageSize: 100,
            pageIndex: 1,
          }),
        ])

        const allLiveAccounts = (liveResponse?.items || []).map((account) => ({
          ...account,
          accountGroupType: account.accountGroupType || {
            id: AccountGroupType.Live,
            name: 'Live',
          },
        }))

        const allDemoAccounts = (demoResponse?.items || []).map((account) => ({
          ...account,
          accountGroupType: account.accountGroupType || {
            id: AccountGroupType.Demo,
            name: 'Demo',
          },
        }))

        const activeAccounts = [...allLiveAccounts, ...allDemoAccounts].filter(
          (account) =>
            account.status?.id !== AccountGroupType.Archived &&
            account.status?.id !== AccountGroupType.Disabled
        )

        setAllActiveAccounts(activeAccounts)

        const tmtOrTradingViewAccounts = activeAccounts.filter(
          (account) =>
            account.platformType.id === PlatformTypeEnum.TickmillTrader ||
            account.platformType.id === PlatformTypeEnum.TradingView
        )

        const selectableAccounts = tmtOrTradingViewAccounts.filter(
          (a) => !isTradingAccountStatusReadOnly(a.status)
        )

        if (selectableAccounts.length === 0) {
          setShowAccountOpening(true)
          setLoading(false)
          return
        }

        const lastAccountId = await getLastLoggedInAccountId()
        const stateAccountId = location.state?.selectedTradingAccount?.id

        let accountToLogin = selectableAccounts.find(
          (a) => a.id === (stateAccountId ?? lastAccountId)
        )

        if (!accountToLogin && !stateAccountId) {
          const tmtLiveAccounts = selectableAccounts.filter(
            (a) =>
              a.platformType.id === PlatformTypeEnum.TickmillTrader &&
              a.accountGroupType?.id === AccountGroupType.Live
          )
          const tmtDemoAccounts = selectableAccounts.filter(
            (a) =>
              a.platformType.id === PlatformTypeEnum.TickmillTrader &&
              a.accountGroupType?.id === AccountGroupType.Demo
          )
          const tradingViewLiveAccounts = selectableAccounts.filter(
            (a) =>
              a.platformType.id === PlatformTypeEnum.TradingView &&
              a.accountGroupType?.id === AccountGroupType.Live
          )
          const tradingViewDemoAccounts = selectableAccounts.filter(
            (a) =>
              a.platformType.id === PlatformTypeEnum.TradingView &&
              a.accountGroupType?.id === AccountGroupType.Demo
          )

          accountToLogin =
            tmtLiveAccounts[0] ||
            tmtDemoAccounts[0] ||
            tradingViewLiveAccounts[0] ||
            tradingViewDemoAccounts[0] ||
            selectableAccounts[0]
        }

        if (accountToLogin) {
          await loginToAccount(accountToLogin)
        }

        setLoading(false)
      } catch (err) {
        console.error('Markets login init failed:', err)
        setError('general_error')
        setLoading(false)
      }
    }

    init()
  }, [entity, apiClient, loginToAccount, location.state?.selectedTradingAccount])

  return {
    loading,
    error,
    isLoggedIn,
    showAccountOpening,
    selectedAccount,
    allActiveAccounts,
    setSelectedAccount: updateSelectedAccount,
  }
}

export type UseTradeLoginReturn = ReturnType<typeof useTradeLogin>

const getLastLoggedInAccountId = async (): Promise<string | null> => {
  try {
    const data = safeLocalStorage.getItem('lastLoggedInTMTAccountId')
    return data || null
  } catch {
    return null
  }
}

const saveLastLoggedInAccount = async (account: Account): Promise<void> => {
  safeLocalStorage.setItem('lastLoggedInTMTAccountId', account.id)
}
